<template>
    <div>
        <div v-if="!selected" class="tutorial-list">
            <div v-for="tutorial in tutorials" :key="tutorial.slug" class="tutorial-item" v-ripple
                @click="$emit('select', tutorial)">
                <span>{{ titleOf(tutorial) }}</span>
                <v-icon size="16">mdi-chevron-right</v-icon>
            </div>
            <div v-if="tutorials.length === 0" class="tutorial-empty">
                {{ $t('common.without_tutorials') }}
            </div>
        </div>
        <div v-else class="tutorial-video-view">
            <button type="button" class="tutorial-back" @click="$emit('back')">
                <v-icon size="16">mdi-chevron-left</v-icon>
                {{ $t('common.back') }}
            </button>
            <div class="tutorial-video-wrap">
                <video-player ref="videoPlayer" :options="playerOptions" />
            </div>
        </div>
    </div>
</template>

<script>
import VideoPlayer from '@/components/common/VideoPlayer'
import { localizedField } from '@/utils/Tutorials'

export default {
    name: 'TutorialList',
    components: { VideoPlayer },
    props: {
        tutorials: {
            type: Array,
            default: () => [],
        },
        selected: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            playerOptions: {
                fluid: false,
                language: 'es',
                controls: true,
                autoplay: false,
                poster: null,
                sources: [{
                    type: 'application/x-mpegURL',
                    src: null,
                }],
            },
        }
    },
    methods: {
        titleOf(tutorial) {
            return localizedField(tutorial, 'title', this.$i18n.locale) || ''
        },
        pause() {
            const player = this.$refs.videoPlayer?.player
            if (player) player.pause()
        },
        loadSelected() {
            const vm = this
            if (!vm.selected) return
            const video = localizedField(vm.selected, 'video', vm.$i18n.locale)
            const thumb = localizedField(vm.selected, 'thumb', vm.$i18n.locale)
            vm.playerOptions.poster = thumb
            vm.playerOptions.sources[0].src = video
            vm.$nextTick(() => {
                const player = vm.$refs.videoPlayer?.player
                if (!player) return
                player.pause()
                player.poster(thumb)
                player.src({ type: 'application/x-mpegURL', src: video })
            })
        },
    },
    watch: {
        selected: {
            immediate: true,
            handler(val, oldVal) {
                if (!val) return this.pause()
                if (oldVal) this.pause()
                this.loadSelected()
            },
        },
    },
}
</script>

<style scoped>
.tutorial-list {
    display: flex;
    flex-direction: column;
    margin: -20px 0;
}

.tutorial-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid var(--v-outline-base);
    cursor: pointer;
    font-size: 15px;
    color: var(--v-txtPrimary-base);
    transition: color 0.12s;
}

.tutorial-item:last-child {
    border-bottom: none;
}

.tutorial-item:hover {
    color: var(--v-secondary-base);
}

.tutorial-item .v-icon {
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.tutorial-empty {
    text-align: center;
    padding: 24px 0;
    font-size: 14px;
    color: var(--v-txtSecondary-base);
}

.tutorial-back {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-bottom: 16px;
    transition: color 0.12s;
    text-transform: capitalize;
}

.tutorial-back:hover {
    color: var(--v-txtPrimary-base);
}

.tutorial-back .v-icon {
    color: inherit;
}

.tutorial-video-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
}

.tutorial-video-wrap >>> #player,
.tutorial-video-wrap >>> .video-js {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    max-height: none !important;
    border-radius: 8px;
}

.tutorial-video-wrap >>> .vjs-tech,
.tutorial-video-wrap >>> .vjs-poster {
    object-fit: contain;
    background-size: contain;
}
</style>
